import { memo } from '@tanstack/react-table';
import { useMemo } from 'react';

import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { isDev } from '../../../../core/utils/is-node-environment.js';
import { useDataTableV2ConfigElement } from '../../../hooks/useDataTableV2Config.js';
import { isGroupColumnDef } from '../../hooks/useTable/datatablev2-typeguards.js';
import type {
  Column,
  ColumnDef,
  Table,
  TableFeature,
  TableOptions,
} from '../../hooks/useTable/types.js';
import { getMemoOptions } from '../../hooks/useTable/utils.js';
import type { DataTableV2RowData } from '../../public.api.js';

/**
 * Configuration object for the column visibility state.
 * To define the column visibility for a column, provide the column ID along with the desired boolean value.
 * @public
 */
export type DataTableV2ColumnVisibilityState = Record<string, boolean>;

/**
 * @public
 */
export interface DataTableV2ColumnVisibilityBaseProps {
  /**
   * Callback that is called when the ColumnVisibility state of any column is changed.
   */
  onColumnVisibilityChange?: (
    columnVisibility: DataTableV2ColumnVisibilityState,
  ) => void;
}

/**
 * @public
 */
export interface DataTableV2ColumnVisibilityControlledProps
  extends DataTableV2ColumnVisibilityBaseProps {
  /**
   * Lets you control the ColumnVisibility state of the individual columns.
   */
  columnVisibility?: DataTableV2ColumnVisibilityState;

  /**
   * Lets you set the initial ColumnVisibility state of the individual columns.
   */
  defaultColumnVisibility?: never;
}

/**
 * @public
 */
export interface DataTableV2ColumnVisibilityUncontrolledProps
  extends DataTableV2ColumnVisibilityBaseProps {
  /**
   * Lets you control the ColumnVisibility state of the individual columns.
   */
  columnVisibility?: never;

  /**
   * Lets you set the initial ColumnVisibility state of the individual columns.
   */
  defaultColumnVisibility?: DataTableV2ColumnVisibilityState;
}

/**
 * Interface defining the props on the DataTableV2 that control the ColumnVisibility feature.
 * @public
 */
export type DataTableV2ColumnVisibilityProps =
  | DataTableV2ColumnVisibilityControlledProps
  | DataTableV2ColumnVisibilityUncontrolledProps;

/**
 * Extension interface to extend the tanstack table column visibility state.
 * @internal
 */
export interface DataTableV2VisibilityOptions {
  originalColumnVisibility?: DataTableV2ColumnVisibilityState;
}

/**
 * Extension interface to extend the types of the columnDefinition from the tanstack table.
 * @public
 */
export interface DataTableV2VisibilityColumnDef {
  /**
   * Controls the ability of the given column to toggle it's visibility.
   * This setting will affect if the column will be hidable in column actions or via
   * the modal controls of the DataTableV2 toolbar.
   */
  disableColumnHiding?: boolean;
}

/**
 * @internal
 */
export interface DataTableV2ColumnVisibilityInstance<
  TData extends DataTableV2RowData,
> {
  // Overriding the type of the getVisibleFlatColumns with our internal Column type, to avoid conflicts
  // between react table and our types.
  getVisibleFlatColumns: () => Column<TData, unknown>[];
  // Overriding the type of the getVisibleLeafColumns with our internal Column type, to avoid conflicts
  // between react table and our types.
  getVisibleLeafColumns: () => Column<TData, unknown>[];
  /**
   * Returns an array of all visible leaf-node columns without the builtin prefix/suffix columns.
   */
  getVisibleLeafColumnsWithoutBuiltin: () => Column<TData, unknown>[];
}

/**
 * @internal
 */
export interface DataTableV2ColumnVisibilityColumn<
  TData extends DataTableV2RowData,
> {
  /**
   * Returns an array of all visible leaf-node columns for this column. If a column has no children, it is considered the only leaf-node column.
   */
  getVisibleLeafColumns: () => Column<TData, unknown>[];
}

/**
 * Helper function to not duplicate this logic for the slightly different use cases of user actions and column settings modal.
 * @internal
 */
export function getCanHide<TData>(
  table: Table<TData>,
  column: Column<TData, unknown>,
  isHideable: (columns: Column<TData, unknown>[]) => boolean,
): boolean {
  // The feature is disabled altogether.
  if (table.options.enableHiding === false) {
    return false;
  }

  // It is a leaf column, just look at the `enableHiding` for this column
  if (!column.columns || column.columns.length === 0) {
    return column.columnDef.enableHiding ?? true;
  }

  // It has sub-columns.
  // `enableHiding` will be false if its explicitly set to false
  // Otherwise check the provided function
  return column.columnDef.enableHiding === false
    ? false
    : isHideable(column.columns);
}

/**
 * Additional feature implementation for column visibility.
 * @internal
 */
export const DataTableV2ColumnVisibility: TableFeature = {
  createTable<TData extends DataTableV2RowData>(table: Table<TData>): void {
    // Helper function that provides an array of all visible leaf-node columns, excluding builtin columns.
    table.getVisibleLeafColumnsWithoutBuiltin = memo(
      () => [table.getVisibleLeafColumns()],
      (visibleLeafColumns) => {
        return visibleLeafColumns.filter(
          (leafColumn) => !leafColumn.columnDef.meta?.builtin,
        );
      },
      {
        key: isDev() && 'getVisibleLeafColumnsWithoutBuiltin',
      },
    );
  },

  createColumn<TData extends DataTableV2RowData>(
    column: Column<TData, unknown>,
    table: Table<TData>,
  ) {
    // Helper function that provides an array of all visible leaf-node columns for this column.
    column.getVisibleLeafColumns = memo(
      () => [column.getLeafColumns(), table.getState().columnVisibility],
      (leafColumns) => {
        return leafColumns.filter((leafColumn) => leafColumn.getIsVisible());
      },
      getMemoOptions(
        table.options,
        'debugColumns',
        'column.getVisibleLeafColumns',
      ),
    );

    column.getCanHide = () =>
      getCanHide(table, column, (columns) =>
        columns.every((c) => c.columnDef.enableHiding),
      );
  },
};

/**
 * Configuration hook for the DataTableV2 ColumnVisibility feature.
 * @internal
 */
export function useColumnVisibility<TData extends DataTableV2RowData>(
  props: DataTableV2ColumnVisibilityProps,
  options: TableOptions<TData>,
) {
  const columnVisibilityConfig =
    useDataTableV2ConfigElement('columnVisibility');

  const [columnVisibility, onColumnVisibilityChange] = useControllableState({
    value: props.columnVisibility ?? columnVisibilityConfig,
    defaultValue: props.defaultColumnVisibility,
    onChange: props.onColumnVisibilityChange,
  });

  options.state = { ...options.state, columnVisibility };
  options.onColumnVisibilityChange = onColumnVisibilityChange;
  options.originalColumnVisibility = useMemo(() => {
    return (
      props.defaultColumnVisibility ??
      getColumnVisibilityFromOriginalColumns(options.columns)
    );
  }, [props.defaultColumnVisibility, options.columns]);
}

/**
 * Helper function that generates visibility state from original columns.
 * @internal
 */
function getColumnVisibilityFromOriginalColumns<
  TData extends DataTableV2RowData,
>(columnDefs: ColumnDef<TData, unknown>[]): DataTableV2ColumnVisibilityState {
  const visibilityState: DataTableV2ColumnVisibilityState = {};

  for (const columnDef of columnDefs) {
    if (isGroupColumnDef(columnDef) && columnDef.columns) {
      const subColumnDefs = columnDef.columns;
      for (const subColumnDef of subColumnDefs) {
        if (subColumnDef.id) {
          visibilityState[subColumnDef.id] = true;
        }
      }
    }
    // In our scenarios, we enforce id's on everything, but based on the tanstack types,
    // they could still be undefined, hence the check here.
    else if (columnDef.id) {
      visibilityState[columnDef.id] = true;
    }
  }

  return visibilityState;
}
